/**
 * Auth schemas — mirror `auth.users`, `auth.workspaces`,
 * `auth.workspace_memberships` per ARCHITECTURE_v5 §4.2.
 *
 * These schemas are reused on three sides of the boundary:
 *   - frontend (apps/chaos-ui) — form validation + typed API client
 *   - gateway (apps/chaos-gateway) — request body validation
 *   - auth service (apps/chaos-auth-service) — drizzle-kit codegen reference
 *
 * Update `auth/migrations/*` together with this file (Phase 1).
 */
import { z } from 'zod';

// Roles available within a workspace. Mirrors auth.workspace_memberships.role
// CHECK constraint. Adding a new role requires a sibling-bumped migration
// and a coordinated update of permissions middleware in chaos-gateway.
export const workspaceRoles = [
  'founder',
  'admin',
  'employee',
  'external',
  'auditor',
  'tuner',
] as const;
export const workspaceRoleSchema = z.enum(workspaceRoles);
export type WorkspaceRole = z.infer<typeof workspaceRoleSchema>;

// auth.users (display surface)
export const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().min(1).max(120).nullable(),
  isActive: z.boolean(),
  emailVerified: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable(),
});
export type User = z.infer<typeof userSchema>;

// auth.workspaces
export const workspaceSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits, single hyphen separators'),
  createdAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
});
export type Workspace = z.infer<typeof workspaceSchema>;

// auth.workspace_memberships
export const workspaceMembershipSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  userId: z.string().uuid(),
  role: workspaceRoleSchema,
  joinedAt: z.string().datetime(),
  leftAt: z.string().datetime().nullable(),
});
export type WorkspaceMembership = z.infer<typeof workspaceMembershipSchema>;

// auth.sessions — exposed to the client only via the refresh-rotation
// endpoint; the JWT body itself never contains the refresh token hash.
export const sessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
});
export type Session = z.infer<typeof sessionSchema>;

// === Password policy (ADR-053) ============================================
// Minimum 14 chars, must include upper + lower + digit + symbol. HIBP check
// runs server-side in chaos-auth-service (k-anonymity: only first 5 chars of
// the SHA-1 hash leave the network).
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{14,}$/;
export const passwordSchema = z
  .string()
  .min(14, 'Password must be at least 14 characters long')
  .max(256, 'Password must be at most 256 characters long')
  .regex(PASSWORD_REGEX, 'Password must contain upper, lower, digit and symbol characters');

// === Request payloads =====================================================
export const signupRequestSchema = z.object({
  email: z.string().email(),
  password: passwordSchema,
  displayName: z.string().min(1).max(120),
});
export type SignupRequest = z.infer<typeof signupRequestSchema>;

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

// Password-reset schemas removed in Phase 1.5 (ADR-053 v1.1).
// Internal tool: founder manages passwords out-of-band via direct DB access.

// === Response payloads ====================================================
export const authSessionResponseSchema = z.object({
  user: userSchema,
  workspace: workspaceSchema,
  membership: workspaceMembershipSchema,
  accessToken: z.string().min(20),
  // Refresh tokens live in httpOnly cookies; this field is for non-browser
  // clients (CLI / Telegram bridge) and may be omitted in the cookie flow.
  refreshToken: z.string().min(20).optional(),
});
export type AuthSessionResponse = z.infer<typeof authSessionResponseSchema>;

export const meResponseSchema = z.object({
  user: userSchema,
  workspaces: z.array(
    z.object({
      workspace: workspaceSchema,
      membership: workspaceMembershipSchema,
    }),
  ),
});
export type MeResponse = z.infer<typeof meResponseSchema>;
